import { Currency } from 'dinero.js'
import { defineStore, Pinia } from 'pinia'
import { ref } from 'vue'
import { getCookie, removeCookie } from '~/utility/cookies'
import { convertMoneyCurrency, MoneyType } from '~/utility/money'

interface CurrencyResponse {
  code: Currency
  name: string
  symbol: string
  rate: number
}

let currenciesCache: Record<string, CurrencyResponse> = {}

export const useCurrencyStore = defineStore('currencyStore', () => {
  const { $i18n } = useNuxtApp()
  const currencies = ref<Record<string, CurrencyResponse>>(currenciesCache)
  const userDataStore = useUserDataStore(usePinia() as Pinia)

  const currentCurrencyCode = ref(
    userDataStore.globalSettings?.currency || $i18n.localeInfo.currency,
  )

  onMounted(() => {
    if (!userDataStore.globalSettings?.currency) {
      _handleCurrencyCookie()
    }
    if (userDataStore.globalSettings?.currency) {
      currentCurrencyCode.value = userDataStore.globalSettings?.currency
    }
  })

  async function load() {
    if (Object.keys(currencies.value).length) {
      return
    }
    try {
      const url = '/_/fallback/_/currencies/'
      const resp = await $fetch<CurrencyResponse[]>(url, {
        timeout: 20000,
      })
      const mapped = resp.reduce((mapped, currency) => {
        mapped[currency.code] = currency
        return mapped
      }, {})
      currencies.value = mapped
      currenciesCache = mapped
      return mapped
    } catch (error) {
      // Ensure we reject with a proper Error object
      if (error instanceof Error) {
        throw error
      }
      throw new Error(
        `Failed to load currencies: ${error ? String(error) : 'Unknown error'}`,
      )
    }
  }

  function _handleCurrencyCookie() {
    const cookieCurrency = getCookie('pu-currency')
    if (cookieCurrency) {
      setCurrentCurrency(cookieCurrency)
      // remove legacy currency cookie
      removeCookie('pu-currency')
    }
  }

  function convert(
    priceToConvert: MoneyType,
    toCurrencyCode: string,
  ): MoneyType {
    if (priceToConvert.getCurrency() === toCurrencyCode) {
      return priceToConvert
    }

    // If currencies aren't loaded yet, return original price
    if (!Object.keys(currencies.value).length) {
      return priceToConvert
    }

    const fromCurrency = currencies.value[priceToConvert.getCurrency()]
    const toCurrency = currencies.value[toCurrencyCode]

    // Validate we have the required currency data
    if (!fromCurrency || !toCurrency) {
      console.warn(
        `Currency conversion failed: missing currency data for ${priceToConvert.getCurrency()} or ${toCurrencyCode}`,
      )
      return priceToConvert
    }

    try {
      return convertMoneyCurrency(priceToConvert, fromCurrency, toCurrency)
    } catch (error) {
      console.error('Currency conversion error:', error)
      // Return original price on conversion error rather than throwing
      return priceToConvert
    }
  }

  function setCurrentCurrency(code: string) {
    currentCurrencyCode.value = code
    userDataStore.setCurrency(code)
  }

  const currentCurrency = computed(() => {
    return currencies.value[currentCurrencyCode.value] || null
  })

  return {
    convert,
    currencies,
    load,
    currentCurrency,
    currentCurrencyCode,
    setCurrentCurrency,
  }
})
